import { useTranslation } from "react-i18next";

/**
 * next-intl compat shim. `useTranslations("ns")` returns a `t(key, values)`
 * that resolves "ns.key" against the shared catalogs — lets ported pages keep
 * their existing `const t = useTranslations("ns"); t("key")` callsites (57 in
 * the Next app) with a near-zero diff. For a bare `useTranslations()` the key
 * is used as-is.
 *
 * ⚠ The returned `t` is a NEW function on every render. Never put it in a
 * useEffect/useCallback/useMemo dependency array: a hook that sets state would
 * re-run on every render — a silent, unbounded refetch loop (CE-66). Leaving it
 * out is safe, since react-i18next resolves the live language at call time.
 * src/lib/__tests__/web-hook-deps-no-translator.test.ts enforces this.
 */
export function useTranslations(namespace?: string) {
  const { t } = useTranslation();
  // next-intl's `t` returns `string`; pin the shim to that so ported pages can
  // render `{t("k")}` as JSX children and pass it to `setState<string>`. Newer
  // react-i18next (15.7+) widens `t()`'s return to TFunctionDetailedResult for
  // non-literal keys, which isn't a valid ReactNode — the `as string` restores
  // the original next-intl contract centrally (no per-page edits).
  return (key: string, values?: Record<string, unknown>): string =>
    t(namespace ? `${namespace}.${key}` : key, values as never) as unknown as string;
}

export default useTranslations;
